import {useForm} from 'react-hook-form'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {errorHandler} from '@utilities/helpers.ts'
import {httpChangePassword} from '@features/user/http/user.http.ts'
import {InputText} from '@components/commons/input-text'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    ModalHeaderTitle,
    ModalParagraph,
    ModalTitle
} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {useAsync} from '@hooks/useAsync.ts'
import {toast} from '@utilities/toast'

const ValidationSchema = z
    .object({
        oldPassword: z.string({required_error: 'errors:required'}).min(1, 'errors:required'),
        newPassword: z.string({required_error: 'errors:required'}).min(8, 'errors:password_invalid_length'),
        confirmedNewPassword: z.string({required_error: 'errors:required'})
    })
    .superRefine((values, context) => {
        if (values.newPassword != values.confirmedNewPassword) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'commons:confirm_password_error',
                path: ['confirmedNewPassword']
            })
        }
    })
type ValidationSchema = z.infer<typeof ValidationSchema>

export const ChangePasswordModal = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const changePassword = useAsync()
    const form = useForm<ValidationSchema>({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: zodResolver(ValidationSchema)
    })
    const handleSubmit = form.handleSubmit(async formValues => {
        try {
            await changePassword.run(
                httpChangePassword({
                    payload: {
                        current_password: formValues.oldPassword,
                        new_password: formValues.newPassword,
                        password_confirmation: formValues.confirmedNewPassword
                    }
                })
            )
            toast.success(t('settings:personal_information:password_changed'))
            onClose()
        } catch (error) {
            errorHandler(error)
        }
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="w-110 grid grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
                showCloseButton={false}
            >
                <ModalHeader className="grid grid-cols-[32px_1fr_32px] items-center justify-center border-none">
                    <DialogClose
                        data-slot="dialog-close"
                        render={<ModalCloseButton disabled={changePassword.isLoading} />}
                    />
                    <ModalHeaderTitle className="p-0">{t('settings:change_password')}</ModalHeaderTitle>
                </ModalHeader>

                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('settings:personal_information:change_password_title')}</ModalTitle>
                        <ModalParagraph>
                            {t('settings:personal_information:change_password_description')}
                        </ModalParagraph>
                    </div>
                    <InputText
                        label={t('settings:personal_information:current_password')}
                        type="password"
                        errorMessage={t((form.formState.errors.oldPassword?.message ?? '') as TKey)}
                        {...form.register('oldPassword')}
                        visibilityToggle
                    />
                    <InputText
                        label={t('commons:new_password')}
                        type="password"
                        errorMessage={t((form.formState.errors.newPassword?.message ?? '') as TKey)}
                        {...form.register('newPassword')}
                        visibilityToggle
                    />
                    <InputText
                        label={t('commons:confirm_password')}
                        type="password"
                        errorMessage={t((form.formState.errors.confirmedNewPassword?.message ?? '') as TKey)}
                        {...form.register('confirmedNewPassword')}
                        visibilityToggle
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="tertiary" onClick={onClose} disabled={changePassword.isLoading}>
                        {t('commons:cancel')}
                        {changePassword.isLoading && <Spinner />}
                    </Button>
                    <Button onClick={handleSubmit} disabled={changePassword.isLoading}>
                        {t('settings:change_password')}
                        {changePassword.isLoading && <Spinner />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
